import { Redux } from 'src/store'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { Resto365FAQ, Resto365FAQList } from '@/src/types/apps/faq'

import {
  RESTO365_FAQ,
  RESTO365_FAQ_BY_ID,
  RESTO365_FAQ_CATEGORY,
  RESTO365_FAQ_CREATE,
  RESTO365_FAQ_DELETE,
  RESTO365_FAQ_LIST,
  RESTO365_FAQ_UPDATE
} from './action'

interface InitialState {
  resto365Faqs: Resto365FAQList[]
  resto365Faq: Resto365FAQList | null
  resto365FaqCat: string[]
  pending: boolean
  errors: any
  success?: string
}

const initialState: InitialState = {
  resto365Faqs: [],
  resto365FaqCat: [''],
  resto365Faq: null,
  errors: null,
  success: undefined,
  pending: false
}

interface FaqResponse {
  data: Resto365FAQList
}

interface FaqListResponse {
  data: Resto365FAQList[]
}

interface FaqCategoryResponse {
  data: string[]
}

interface FaqUpdatePayload {
  id: number
  data: Resto365FAQList
}

export const gygResto365FAQCategory = createAsyncThunk<FaqCategoryResponse, void, Redux>(
  RESTO365_FAQ_CATEGORY,
  async (_, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-faq/listFaqCategories/categories`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const createResto365FAQ = createAsyncThunk<FaqResponse, Resto365FAQ, Redux>(
  RESTO365_FAQ_CREATE,
  async (faqData, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.post('resto365-faq', faqData)
      const data = await response.data

      return data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const gygResto365FAQList = createAsyncThunk<FaqListResponse, void, Redux>(
  RESTO365_FAQ_LIST,
  async (_, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-faq`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const gygResto365FAQById = createAsyncThunk<FaqResponse, string, Redux>(
  RESTO365_FAQ_BY_ID,
  async (Id: string, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-faq/${Id}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const updateResto365FAQ = createAsyncThunk<FaqResponse, FaqUpdatePayload, Redux>(
  RESTO365_FAQ_UPDATE,
  async ({ id, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-faq/${id}`, { method: 'PATCH', data })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const deleteResto365FAQ = createAsyncThunk<FaqResponse, number, Redux>(
  RESTO365_FAQ_DELETE,
  async (id: number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-faq/${id}`, { method: 'DELETE' })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const resto365FaqsData = createSlice({
  name: RESTO365_FAQ,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      // Get All FAQ's Category
      .addCase(gygResto365FAQCategory.fulfilled, (state, action) => {
        state.resto365FaqCat = action.payload.data
        state.success = undefined
        state.pending = false
        state.errors = null
      })
      .addCase(gygResto365FAQCategory.pending, state => {
        state.success = undefined
        state.pending = true
      })
      .addCase(gygResto365FAQCategory.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      // Create FAQ
      .addCase(createResto365FAQ.fulfilled, (state, action) => {
        state.resto365Faq = action.payload.data
        state.success = 'FAQ created Successfully'
        state.errors = null
        state.pending = false
      })
      .addCase(createResto365FAQ.pending, state => {
        state.pending = true
        state.success = undefined
      })
      .addCase(createResto365FAQ.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      // Get All FAQ's
      .addCase(gygResto365FAQList.fulfilled, (state, action) => {
        state.resto365Faqs = action.payload.data
        state.success = undefined
        state.pending = false
        state.errors = null
      })
      .addCase(gygResto365FAQList.pending, state => {
        state.success = undefined
        state.pending = true
      })
      .addCase(gygResto365FAQList.rejected, state => {
        state.success = undefined
        state.pending = false
      })

      // Get All FAQ's BY Id
      .addCase(gygResto365FAQById.fulfilled, (state, action) => {
        state.resto365Faq = action.payload.data
        state.success = undefined
        state.pending = false
        state.errors = null
      })
      .addCase(gygResto365FAQById.pending, state => {
        state.success = undefined
        state.pending = true
      })
      .addCase(gygResto365FAQById.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      // Update FAQ
      .addCase(updateResto365FAQ.fulfilled, (state, action) => {
        state.resto365Faq = action.payload.data
        state.success = 'FAQ updated Successfully'
        state.errors = null
        state.pending = false
      })
      .addCase(updateResto365FAQ.pending, state => {
        state.pending = true
        state.success = undefined
      })
      .addCase(updateResto365FAQ.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      // Delete FAQ
      .addCase(deleteResto365FAQ.fulfilled, (state, action) => {
        state.resto365Faq = action.payload.data
        state.success = 'FAQ Deleted Successfully'
        state.errors = null
        state.pending = false
      })
      .addCase(deleteResto365FAQ.pending, state => {
        state.pending = true
        state.success = undefined
      })
      .addCase(deleteResto365FAQ.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })
  }
})

export default resto365FaqsData.reducer
